import { Box, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { QuestionType } from "../../../../constants";
import { GetSurveyCoherenceReportResultDto } from "../../../../http";
import { BinaryResult } from "./BinaryResult";
import { LinearScaleResult } from "./LinearScaleResult";
import { LinearScaleResultSkeleton } from "./LinearScaleResultSkeleton";
import { QuestionFilter } from "./QuestionFilter";
import { useBreakpoints } from "src/hooks";

interface IProps {
  isLoading: boolean;
  report: GetSurveyCoherenceReportResultDto | undefined;
  selectedLabel: string;
  comparisonLabel?: string;
}

const LinearScaleResults = (props: IProps) => {
  const [selectedQuestionGuids, setSelectedQuestionGuids] = useState<string[]>([]);
  const { isMdUp } = useBreakpoints();

  useEffect(() => {
    // Select all questions by default.
    setSelectedQuestionGuids(
      Object.entries(props.report?.questions ?? {})
        .filter(([, question]) => question.questionTypeId === QuestionType.LinearScale)
        .map(([questionGuid]) => questionGuid),
    );
  }, [props.report]);

  return (
    <Stack sx={{ height: "100%", overflow: "hidden" }}>
      {isMdUp && (
        <>
          <QuestionFilter
            questions={Object.entries(props.report?.questions ?? {})
              .filter(([, question]) => question.questionTypeId === QuestionType.LinearScale)
              .map(([questionGuid, question]) => ({
                questionGuid: questionGuid,
                questionName: question.name,
                questionTags: question.tags,
              }))}
            selectedQuestionGuids={selectedQuestionGuids}
            onChange={(values) => setSelectedQuestionGuids(values)}
            disabled={props.isLoading || props.report === undefined}
          />
          <Divider />
        </>
      )}

      <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, py: 1 }}>
        <Stack
          spacing={1}
          divider={<Divider variant="dashed" sx={{ color: "#c9c9c9" }} />}
          sx={{ px: { xs: 0.5, md: 1 } }}
        >
          {props.isLoading ? (
            <LinearScaleResultSkeleton sx={{ px: { xs: 0.5, md: 1 }, py: 0.5 }} />
          ) : props.report?.linearScaleResponses.some((x) => selectedQuestionGuids.includes(x.questionGuid)) ? (
            props.report?.linearScaleResponses
              .filter((x) => selectedQuestionGuids.includes(x.questionGuid))
              .map((x, index) => {
                const question = props.report?.questions[x.questionGuid];

                if (question === undefined) {
                  return null;
                }

                if (question.maxPoints === 2) {
                  return (
                    <BinaryResult
                      key={index}
                      question={{
                        name: question.name,
                        description: question.description,
                        tags: question.tags,
                        maxPoints: question.maxPoints,
                        minLabel: question.minLabel,
                        maxLabel: question.maxLabel,
                      }}
                      selectedLabel={props.selectedLabel}
                      comparisonLabel={props.comparisonLabel}
                      selectedAverage={x.selectedTeamsAverage}
                      comparisonTeamsAverage={x.comparisonTeamsAverage}
                    />
                  );
                } else {
                  return (
                    <LinearScaleResult
                      key={index}
                      question={{
                        name: question.name,
                        description: question.description,
                        tags: question.tags,
                        maxPoints: question.maxPoints,
                        minLabel: question.minLabel,
                        maxLabel: question.maxLabel,
                      }}
                      selectedLabel={props.selectedLabel}
                      comparisonLabel={props.comparisonLabel}
                      selectedAverage={x.selectedTeamsAverage}
                      comparisonAverage={x.comparisonTeamsAverage}
                    />
                  );
                }
              })
          ) : (
            <Typography sx={{ px: { xs: 0.5, md: 1 }, py: 0.5 }}>
              No responses for selected questions and entities.
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export { LinearScaleResults };
